import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet } from '../../../../components/Sheet';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { ART_STYLE } from '../../../../theme/tokens';
import type { ArtStyle } from '../../art-style-api';
import type { UseArtStyle } from '../../useArtStyle';
import { ExtractBar } from '../ExtractBar';
import { IngredientsBody, type IngredientsBodyProps } from '../IngredientsBody';
import { SheetHeading } from './IngredientsSheet.styles';

export type IngredientsSheetProps = Omit<IngredientsBodyProps, 'art'> & {
  art: IngredientsBodyProps['art'] & Pick<UseArtStyle, 'busy' | 'extracting' | 'extractFailed'>;
  /** The style's first definition has landed and the sheet is about to become the rail: until then it holds still,
   *  as it was while Lumio extracted. */
  landing: boolean;
  /** Credits an extraction costs (it draws the four samples). */
  cost: number | undefined;
  onExtract: () => void;
  onClose: () => void;
};

/*
 * Where the FTUX's art style piece lights the sheet (inert outside an FTUX step). The shared Sheet forwards no
 * attribute, so the mark sits on the body, and the piece's zone climbs from it to the sheet's dialog, heading and
 * footer included. A plain block round the body: the sheet lays it out as it laid out the body.
 */
const INGREDIENTS_TARGET: FtuxTargetId = 'style-ingredients';

/**
 * A style's ingredients before its first extraction: the Ingredients heading, the accordion and Extract, in a sheet
 * over the studio (a centred dialog, a bottom sheet on a phone). It can be closed at any time, an extraction included
 * (the card behind says what Lumio is doing and opens it again). When the definition lands with it open, the page
 * turns it into the studio's rail (useIngredientsMorph). Its content takes the sheet's full width: on a desktop the
 * sheet is sized so that is about the rail's, and the content lands without reflowing.
 */
export const IngredientsSheet = ({
  style,
  art,
  landing,
  openSection,
  onToggleSection,
  cost,
  onExtract,
  onClose,
}: IngredientsSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  // The definition lands a frame before the morph captures the sheet: the footer keeps showing the extraction (not
  // "Extract again", not gone), so what the morph starts from is what the owner was looking at.
  const shown = landing ? { ...style, definition: '' } : style;

  return (
    <Sheet
      header={<SheetHeading title={t('Ingredients')} />}
      ariaLabel="Ingredients"
      onClose={onClose}
      maxWidth={`${ART_STYLE.SHEET_WIDTH_PX}px`}
      footer={
        <ExtractBar
          placement="sheet"
          style={shown}
          busy={art.busy}
          extracting={art.extracting || landing}
          lastFailed={art.extractFailed}
          cost={cost}
          onExtract={onExtract}
        />
      }
    >
      <div data-ftux={INGREDIENTS_TARGET}>
        <IngredientsBody style={style} art={art} openSection={openSection} onToggleSection={onToggleSection} />
      </div>
    </Sheet>
  );
};

export default IngredientsSheet;
